// Shared shapes for the JSON-path sample/runner/cache pipeline.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C4.

import type { SqlEngine } from '../../../sql-grammar/model';

export type SampleTarget = {
  readonly engine: SqlEngine;
  readonly schema: string;
  readonly table: string;
  readonly column: string;
};

// Engine-correct sample-paths SQL plus the cap that produced it.
// `maxRows` (JCS-9 "≤256 rows") is enforced upstream via the LIMIT clause in
// the dialect-generated SQL — the runner never sees raw source rows, only
// already-aggregated path/frequency tuples. `maxPaths` (JCS-9 "≤64 distinct
// paths") is enforced in the runner (sort + truncate before caching) and
// re-enforced in mergeWithDeclaredHints as a source-level guarantee. Both
// values are exported so the dialog can show "Sample 256 rows?" verbatim.
export type SampleQuery = {
  readonly sql: string;
  readonly maxRows: number;
  readonly maxPaths: number;
};

// A single JSON path discovered by the scan. `frequency` is the sample-side
// observation count — the runner ranks the returned list by `frequency desc`
// before truncating to `maxPaths`. Path strings are stored verbatim as the
// engine returned them (PG / Oracle / SQLite write `$.foo.bar`; MySQL writes
// `$.foo.bar` via JSON_EXTRACT; MSSQL writes `$.foo` via OPENJSON keys with a
// leading `$.` synthesized at the helper layer for uniform shape).
export type SampledPath = {
  readonly path: string;
  readonly frequency: number;
};

// What lands in the per-session cache after a scan completes. `scannedAt` is
// the ISO timestamp of the scan; consumers display it as "Sampled at …" so
// users know how fresh the hints are.
export type PathHintsCacheEntry = {
  readonly target: SampleTarget;
  readonly paths: readonly SampledPath[];
  readonly sampleSize: number;
  readonly scannedAt: string;
};

// JCS-9 caps. Exported so the dialog + runner share a single source of truth.
export const SAMPLE_ROW_CAP = 256 as const;
export const SAMPLE_PATH_CAP = 64 as const;
